import { SolarAnalysis } from "../services/portfolioApi"

const parseNumber = (value: unknown) =>
  value !== undefined && value !== null ? Number(value) : 0

const getField = (
  analysis: SolarAnalysis,
  camelCase: string,
  snakeCase: string
) =>
  (analysis as any)?.[camelCase] ?? (analysis as any)?.[snakeCase] ?? 0

const orientationConfig = [
  { key: "South", camelSuffix: "South", snakeSuffix: "_south" },
  { key: "Southeast", camelSuffix: "Southeast", snakeSuffix: "_southeast" },
  { key: "Southwest", camelSuffix: "Southwest", snakeSuffix: "_southwest" },
  { key: "East", camelSuffix: "East", snakeSuffix: "_east" },
  { key: "West", camelSuffix: "West", snakeSuffix: "_west" },
  { key: "North", camelSuffix: "North", snakeSuffix: "_north" },
  { key: "Northeast", camelSuffix: "Northeast", snakeSuffix: "_northeast" },
  { key: "Northwest", camelSuffix: "Northwest", snakeSuffix: "_northwest" },
  { key: "Flat", camelSuffix: "Flat", snakeSuffix: "_flat" }
]

export const getFeatureAnalysis = (solarAnalyses: SolarAnalysis[]) =>
  solarAnalyses[0] || null


export const getSolarOverviewMetrics = (analysis: SolarAnalysis | null) => {
  if (!analysis) {
    return {
      recommendedCount: 0,
      maxPanels: 0,
      systemCapacityKw: 0,
      estAnnualGen: 0,
      roofSurfaceSqm: 0,
      sunshineHours: null as null | number,
      panelArea: 0,
      extraPanelsPossible: 0
    }
  }

  const recommendedCount = parseNumber(analysis.recommendedPanelCount)
  const maxPanels = parseNumber(analysis.maximumPanelsPossible)
  const panelWattage = parseNumber(analysis.panelWattage)
  const systemCapacityKw =
    recommendedCount && panelWattage
      ? Number(((recommendedCount * panelWattage) / 1000).toFixed(1))
      : 0

  const existingPanelsKwp = parseNumber(
    analysis.existingPanelsKwp ?? (analysis as any)?.existing_panels_kwp
  )

  return {
    recommendedCount,
    maxPanels,
    systemCapacityKw,
    estAnnualGen: parseNumber(analysis.recommendedPanelsOutputKwh),
    roofSurfaceSqm: parseNumber(analysis.googleSolarRoofSurfaceSqm),
    sunshineHours: (analysis as any)?.googleSolarAnnualSunshineHours ?? null,
    panelArea: parseNumber(analysis.panelAreaSqm),
    extraPanelsPossible: Math.max(maxPanels - recommendedCount, 0),
    existingPanelsKwp
  }
}

export const buildAspectRows = (analysis: SolarAnalysis | null, prefix: "recommended" | "maximum" = "recommended") => {
  if (!analysis) return []

  return orientationConfig.map(({ key, camelSuffix, snakeSuffix }) => {
    const panels = getField(analysis, `${prefix}${camelSuffix}Panels`, `${prefix}${snakeSuffix}_panels`)
    const output = getField(
      analysis,
      `${prefix}${camelSuffix}OutputKwh`,
      `${prefix}${snakeSuffix}_output_kwh`
    )
    const area = getField(
      analysis,
      `roofArea${camelSuffix}Sqm`,
      `roof_area${snakeSuffix}_sqm`
    )
    return {
      label: key,
      panels: parseNumber(panels),
      outputKwh: parseNumber(output),
      areaSqm: parseNumber(area)
    }
  })
}

export const getPanelConfiguration = (analysis: SolarAnalysis | null) => {
  if (!analysis) return { panelWattageDisplay: 0, panelEfficiencyDisplay: 0, panelAreaDisplay: 0 }

  return {
    panelWattageDisplay: parseNumber(
      analysis.panelWattage ?? (analysis as any)?.panel_wattage
    ),
    panelEfficiencyDisplay: parseNumber(
      analysis.panelEfficiencyPercentage ??
        (analysis as any)?.panel_efficiency_percentage
    ),
    panelAreaDisplay: parseNumber(
      analysis.panelAreaSqm ?? (analysis as any)?.panel_area_sqm
    )
  }
}

export const sumAspectValues = (
  aspectRows: Array<{ panels: number; outputKwh: number }>
) => ({
  totalPanels: aspectRows.reduce((sum, row) => sum + (row.panels || 0), 0),
  totalOutput: aspectRows.reduce((sum, row) => sum + (row.outputKwh || 0), 0)
})

/**
 * True when the analysis carries a real per-orientation maximum-panel
 * breakdown (i.e. at least one orientation has maximum panels). Older
 * analyses predate the maximum_*_panels fields and report 0 everywhere.
 */
export const hasMaximumPanelBreakdown = (
  maxAspectRows: Array<{ panels: number }>
) => maxAspectRows.some((row) => row.panels > 0)

/**
 * Max panels to show for a single orientation row.
 *
 * When the analysis has a real maximum breakdown we trust the API value
 * verbatim — including a legitimate 0, which means the orientation can't
 * host panels (too small / obstructed). We only fall back to estimating
 * from roof area for legacy analyses that have no maximum breakdown at all.
 *
 * The previous `apiMaxPanels || areaEstimate` form treated a genuine 0 as
 * "missing" and fabricated panels from area, e.g. a 7.8m² southwest facet
 * with 0 API panels rendered 3 (GRE-155).
 */
export const getRowMaxPanels = ({
  apiMaxPanels,
  hasBreakdown,
  areaSqm,
  panelArea
}: {
  apiMaxPanels: number
  hasBreakdown: boolean
  areaSqm: number
  panelArea: number
}) => {
  if (hasBreakdown) return apiMaxPanels
  return panelArea > 0 ? Math.floor((areaSqm * 0.85) / panelArea) : 0
}
